import { useNavigate } from 'react-router-dom';
import { CardContent } from '@/components/ui';
import type { OrderSide } from '@/analytics';
import type { EventCardDto, MarketCardRowDto, MarketOptionDto } from '@/lib/models';
import { centsToPercent, centsToPayout, formatPayout, formatPercentForSide } from '@/lib/binary-market';
import { useEventLiveStatus } from '@/lib/realtime';
import { useCategoryMeta } from '../CategoryTabs/useCategoryMeta';
import { LiveBadge } from './LiveBadge';
import { LobbyCardRow } from './LobbyCardRow';
import { LobbyEventCard } from './LobbyEventCard';
import { MarketOutcomePricesSubscriber, optionCents, useOutcomePrices, type OutcomePriceMap } from './useOutcomePrices';

export interface BinaryQuestionCardProps {
  /** A single lobby event card from `usePredictionMarketEventsFindLobby`. */
  event: EventCardDto;
  /** Opens the buy-in popup pre-set to the chosen option + side (single binary markets only). */
  onOpenBuy: (option: MarketOptionDto, side: OrderSide) => void;
}

/**
 * Lobby binary-question card (ARC-82 §4). Renders straight from each market's
 * backend-resolved shape (`renderKind` + `options`):
 * - single **BINARY** market → a Yes row + a No row (inline buy popup);
 * - single **MULTI** market → one option row per candidate (tap → market page);
 * - multi-market event → one row per market (tap → market page).
 *
 * Tapping the card navigates to the market page; live prices overlay the seed.
 */
export function BinaryQuestionCard({ event, onOpenBuy }: BinaryQuestionCardProps) {
  const navigate = useNavigate();
  const { prices, setPrice } = useOutcomePrices();

  function goToEvent() {
    navigate(`/events/${event.id}`);
  }

  // Inline buy applies only to a single BINARY market; otherwise a row navigates.
  const inlineBuyMarket = event.isSingleMarket && event.markets[0]?.renderKind === 'BINARY' ? event.markets[0] : null;

  function handleRow(option: MarketOptionDto, side: OrderSide) {
    if (!inlineBuyMarket) return goToEvent();
    onOpenBuy(option, side);
  }

  const first = event.markets[0];
  const isMultiOption = event.isSingleMarket && first?.renderKind === 'MULTI';
  // Live option count for a multi-outcome market (excludes settled); else the market count.
  const count = isMultiOption ? (first?.optionCount ?? 1) : event.marketCount;
  const marketLabel = count === 1 ? '1 Market' : `${count} Markets`;

  // Live (in-play) state — seeded from REST, flipped in-session over the
  // per-event realtime channel. Only SPORTS events can go live, so REGULAR
  // cards pass a null key and never open an event channel.
  const isLive = useEventLiveStatus(event.id, event.eventType === 'SPORTS' ? (event.eventKey ?? null) : null, event.isLive);

  // "Elections – U.S. Elections" eyebrow: category name (+ sub-category when present).
  const categoryMeta = useCategoryMeta(event.categoryCode);
  const eyebrow = categoryMeta
    ? event.subCategoryName
      ? `${categoryMeta.displayName} – ${event.subCategoryName}`
      : categoryMeta.displayName
    : undefined;

  return (
    <LobbyEventCard
      eventId={event.id}
      name={event.name}
      eyebrow={eyebrow}
      eyebrowIconUrl={categoryMeta?.iconImgUrl}
      subtitle={isLive ? <LiveBadge /> : undefined}
      closeTime={event.closeTime}
      volume={event.volume}
      marketLabel={marketLabel}
    >
      {event.markets.map((m) => (
        <MarketOutcomePricesSubscriber key={m.id} marketId={m.id} externalMarketId={m.externalMarketId} onPrice={setPrice} />
      ))}

      <CardContent className="flex-1 gap-0 px-5">
        {event.markets.map((m, i) => (
          <MarketRows
            key={m.id}
            market={m}
            inlineBuy={inlineBuyMarket?.id === m.id}
            prices={prices}
            onRow={handleRow}
            accentOffset={i}
          />
        ))}
      </CardContent>
    </LobbyEventCard>
  );
}

/** Positional purple/blue alternation for Yes-side rows (Figma 5751-141388). */
function accentAt(index: number): 'purple' | 'blue' {
  return index % 2 === 0 ? 'purple' : 'blue';
}

/** Rows for one market: Yes+No (inline-buy binary), one labelled row (multi-market binary), or option rows (MULTI). */
function MarketRows({
  market,
  inlineBuy,
  prices,
  onRow,
  accentOffset,
}: {
  market: MarketCardRowDto;
  inlineBuy: boolean;
  prices: OutcomePriceMap;
  onRow: (option: MarketOptionDto, side: OrderSide) => void;
  /** Row-position offset so the purple/blue alternation continues across markets. */
  accentOffset: number;
}) {
  if (!market.options?.length) return null;

  if (market.renderKind === 'BINARY') {
    const option = market.options[0];
    const cents = optionCents(option, prices);
    if (inlineBuy) {
      // A true Yes/No pair keeps the side semantics: Yes purple, No blue.
      return (
        <>
          <Row option={option} side="yes" cents={cents.yes} label="Yes" onRow={onRow} />
          <Row option={option} side="no" cents={cents.no} label="No" onRow={onRow} />
        </>
      );
    }
    return (
      <Row
        option={option}
        side="yes"
        cents={cents.yes}
        label={market.displayName ?? 'Market'}
        accent={accentAt(accentOffset)}
        onRow={onRow}
      />
    );
  }

  // Rank candidates by LIVE chance so a price tick re-orders the rows (favourites first).
  const ranked = [...market.options].sort((a, b) => (optionCents(b, prices).yes ?? -1) - (optionCents(a, prices).yes ?? -1));
  return (
    <>
      {ranked.map((o, i) => (
        <Row
          key={o.id}
          option={o}
          side="yes"
          cents={optionCents(o, prices).yes}
          label={o.label ?? 'Option'}
          accent={accentAt(accentOffset + i)}
          onRow={onRow}
        />
      ))}
    </>
  );
}

/** One lobby row for an option side. */
function Row({
  option,
  side,
  cents,
  label,
  accent,
  onRow,
}: {
  option: MarketOptionDto;
  side: OrderSide;
  cents: number | null;
  label: string;
  accent?: 'purple' | 'blue';
  onRow: (option: MarketOptionDto, side: OrderSide) => void;
}) {
  return (
    <LobbyCardRow
      label={label}
      percentLabel={formatPercentForSide(centsToPercent(cents), side)}
      payoutLabel={formatPayout(centsToPayout(cents))}
      side={side}
      accent={accent}
      onSelect={() => onRow(option, side)}
    />
  );
}
